import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Space, Typography, Badge, Row, Col, Tooltip, Input, notification, message } from 'antd';
import {
    PhoneOutlined,
    MinusOutlined,
    AudioOutlined,
    AudioMutedOutlined,
    PauseOutlined,
    ArrowLeftOutlined,
    PlayCircleOutlined,
    TableOutlined
} from '@ant-design/icons';
import JsSIP from 'jssip';
import { useAuth } from '../context/AuthContext';
import { useSoftphone } from '../context/SoftphoneContext';
import { userService } from '../services/api';
import './Softphone.css';

const { Text, Title } = Typography;

// DTMF Frequencies
const dtmfFrequencies = {
    '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
    '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
    '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
    '*': [941, 1209], '0': [941, 1336], '#': [941, 1477]
};

// SIP Config
const sipConfig = {
    wss_url: "wss://centralvirtual1.ispcorp.pe:18189/ws",
    realm: "centralvirtual1.ispcorp.pe",
    register: true,
    session_timers: true
};

// Helper function to decrypt payload using Web Crypto API (AES-GCM)
const decryptPayload = async (encryptedPayload, token) => {
    try {
        const json = JSON.parse(atob(encryptedPayload));
        const ciphertext = Uint8Array.from(atob(json.c), c => c.charCodeAt(0));
        const iv = Uint8Array.from(atob(json.iv), c => c.charCodeAt(0));
        const tag = Uint8Array.from(atob(json.t), c => c.charCodeAt(0));

        // Derive key (SHA-256 hash of token, consistent with PHP hash('sha256', $token, true))
        const encoder = new TextEncoder();
        const tokenDigest = await window.crypto.subtle.digest("SHA-256", encoder.encode(token));
        const key = await window.crypto.subtle.importKey(
            "raw",
            tokenDigest,
            { name: "AES-GCM" },
            false,
            ["decrypt"]
        );

        // Concatenate ciphertext + tag for Web Crypto AES-GCM
        const encryptedData = new Uint8Array(ciphertext.length + tag.length);
        encryptedData.set(ciphertext);
        encryptedData.set(tag, ciphertext.length);

        const decrypted = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv: iv },
            key,
            encryptedData
        );

        const decoder = new TextDecoder();
        return JSON.parse(decoder.decode(decrypted));
    } catch (e) {
        console.error("Decryption failed", e);
        return null;
    }
};

const Softphone = () => {
    const { user, isAuthenticated } = useAuth();
    const { 
        minimized, setMinimized, 
        status, setStatus, 
        inCall, setInCall, 
        isCalling, setIsCalling, 
        isRinging, setIsRinging,
        externalNumber, setExternalNumber,
        customDisplay, setCustomDisplay
    } = useSoftphone();
    
    const [muted, setMuted] = useState(false);
    const [onHold, setOnHold] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [dialNumber, setDialNumber] = useState('');
    const [lastDialNumber, setLastDialNumber] = useState('');
    const [activeSession, setActiveSession] = useState(null);
    const [showKeypad, setShowKeypad] = useState(false);
    const [callSummary, setCallSummary] = useState(null);
    // Local state for extension info (since it might be fetched async)
    const [extension, setExtension] = useState(user?.sip_extension?.extension || null);

    const uaRef = useRef(null);
    const sessionRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const audioCtxRef = useRef(null);
    const ringIntervalRef = useRef(null);
    const secondsRef = useRef(0);

    // Helper functions
    const stopRingtone = useCallback(() => {
        if (ringIntervalRef.current) {
            clearInterval(ringIntervalRef.current);
            ringIntervalRef.current = null;
        }
    }, []);

    const playDTMFLocally = useCallback((digit) => {
        try {
            if (!dtmfFrequencies[digit]) return;
            if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            const context = audioCtxRef.current;
            if (context.state === 'suspended') context.resume();
            const [f1, f2] = dtmfFrequencies[digit];
            const osc1 = context.createOscillator();
            const osc2 = context.createOscillator();
            const gainNode = context.createGain();
            osc1.frequency.value = f1; osc2.frequency.value = f2;
            gainNode.gain.setValueAtTime(0.05, context.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.2);
            osc1.connect(gainNode); osc2.connect(gainNode); gainNode.connect(context.destination);
            osc1.start(); osc2.start(); osc1.stop(context.currentTime + 0.2); osc2.stop(context.currentTime + 0.2);
        } catch (err) {}
    }, []);

    const unlockAudio = useCallback(() => {
        if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (remoteAudioRef.current) remoteAudioRef.current.play().catch(() => {});
    }, []);

    const forceAudioAttach = useCallback(() => {
        const session = sessionRef.current;
        if (session && session.connection && remoteAudioRef.current) {
            const streams = session.connection.getRemoteStreams();
            if (streams[0]) {
                remoteAudioRef.current.srcObject = streams[0];
                remoteAudioRef.current.play().catch(() => {});
            }
        }
    }, []);

    const makeCall = useCallback(() => {
        // Redial functionality: if dialNumber is empty, just populate it with lastDialNumber
        if (!dialNumber) {
            if (lastDialNumber) {
                setDialNumber(lastDialNumber);
            }
            return;
        }

        if (!uaRef.current || status !== 'registered') return;
        
        setLastDialNumber(dialNumber);
        unlockAudio();
        setIsCalling(true);
        uaRef.current.call(`sip:${dialNumber}@${sipConfig.realm}`, { mediaConstraints: { audio: true, video: false } });
    }, [dialNumber, lastDialNumber, status, unlockAudio]);

    // Watch for external call requests
    useEffect(() => {
        if (externalNumber && status === 'registered' && !inCall && !isCalling) {
            const numToCall = externalNumber;
            setDialNumber(numToCall);
            setExternalNumber(null);
            
            // Logic to make call
            setTimeout(() => {
                if (!numToCall || !uaRef.current || status !== 'registered') return;
                unlockAudio();
                setIsCalling(true);
                uaRef.current.call(`sip:${numToCall}@${sipConfig.realm}`, { mediaConstraints: { audio: true, video: false } });
            }, 100);
        }
    }, [externalNumber, status, inCall, isCalling, setExternalNumber, setIsCalling, unlockAudio]);


    const playRingtone = useCallback(() => {
        if (ringIntervalRef.current) return;
        const playRing = () => {
            try {
                if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
                const context = audioCtxRef.current;
                const osc1 = context.createOscillator();
                const osc2 = context.createOscillator();
                const gain = context.createGain();
                osc1.frequency.value = 440; osc2.frequency.value = 480;
                gain.gain.setValueAtTime(0, context.currentTime);
                gain.gain.linearRampToValueAtTime(0.1, context.currentTime + 0.05);
                gain.gain.linearRampToValueAtTime(0, context.currentTime + 2);
                osc1.connect(gain); osc2.connect(gain); gain.connect(context.destination);
                osc1.start(); osc2.start(); osc1.stop(context.currentTime + 2); osc2.stop(context.currentTime + 2);
            } catch (e) {}
        };
        playRing();
        ringIntervalRef.current = setInterval(playRing, 3000);
    }, []);

    const formatTime = useCallback((totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }, []);

    const prioritizeOpus = (sdp) => {
        if (!sdp) return sdp;
        let lines = sdp.split(/\r?\n/);
        let mAudioIndex = lines.findIndex(line => line.indexOf('m=audio') === 0);
        if (mAudioIndex !== -1) {
            let mLine = lines[mAudioIndex];
            let elements = mLine.trim().split(/\s+/);
            if (elements.length > 3) {
                let payloads = elements.slice(3);
                let opusPayload = null;
                for (let line of lines) {
                    if (line.indexOf('a=rtpmap:') === 0 && line.toLowerCase().includes('opus')) {
                        let match = line.match(/a=rtpmap:(\d+)\s+opus/i);
                        if (match) { opusPayload = match[1]; break; }
                    }
                }
                if (opusPayload) {
                     payloads = payloads.filter(p => p !== opusPayload);
                     payloads.unshift(opusPayload);
                     lines[mAudioIndex] = `${elements[0]} ${elements[1]} ${elements[2]} ${payloads.join(' ')}`;
                }
            }
        }
        return lines.join('\r\n');
    };





    const handleCallEnded = useCallback((session, event) => {
        const callerNum = session.remote_identity.uri.user;
        const duration = formatTime(secondsRef.current || 0);
        
        // session.isEstablished() seems to return false in the 'ended' event
        // Checking if call had a start time is a improved way to know if it was connected
        const wasEstablished = !!session.start_time;
        
        // Use event data if available, fallback to session properties
        const eventCause = event ? event.cause : session.end_cause;
        const sipMessage = event && event.message;
        const sipCode = sipMessage ? sipMessage.status_code : (session.cause ? session.cause.replace('SIP;cause=', '') : '');
        const sipReasonPhrase = sipMessage ? sipMessage.reason_phrase : '';


        
        setInCall(false);
        setIsCalling(false);
        setIsRinging(false);
        stopRingtone();
        setOnHold(false);
        setActiveSession(null);
        setShowKeypad(false);
        setMuted(false);
        setSeconds(0);
        secondsRef.current = 0;
        setSeconds(0);
        secondsRef.current = 0;
        setDialNumber('');
        setCustomDisplay(null);
        
        if (callerNum) {
            let reason = 'Llamada finalizada';
            
            // Map JsSIP causes to Spanish user-friendly messages
            // See: https://jssip.net/documentation/3.3.x/api/causes/
            switch (eventCause) {
                case JsSIP.C.causes.BUSY:
                    reason = 'Ocupado';
                    break;
                case JsSIP.C.causes.REJECTED:
                    reason = 'Llamada rechazada';
                    // Check for specific SIP codes inside REJECTED
                    if (sipCode === 603) reason = 'Rechazado por destino'; // 603 Decline
                    if (sipCode === 403) reason = 'Prohibido (403)';      // 403 Forbidden
                    if (sipCode === 486) reason = 'Ocupado';              // 486 Busy Here
                    break;
                case JsSIP.C.causes.UNAVAILABLE:
                    reason = 'No disponible';
                    if (sipCode === 480) reason = 'No disponible temporalmente';
                    if (sipCode === 404) reason = 'Número no existe';
                    break;
                case JsSIP.C.causes.NOT_FOUND:
                    reason = 'Número no encontrado';
                    break;
                case JsSIP.C.causes.ADDRESS_INCOMPLETE:
                    reason = 'Número incompleto';
                    break;
                case JsSIP.C.causes.INCOMPATIBLE_DESTINATION:
                     // If call had start_time, it was established.
                    reason = wasEstablished ? 'Llamada finalizada' : 'Destino incompatible'; 
                    break;
                case JsSIP.C.causes.AUTHENTICATION_ERROR:
                    reason = 'Error de autenticación';
                    break;
                case JsSIP.C.causes.REQUEST_TIMEOUT:
                    reason = 'Tiempo de espera agotado';
                    break;
                case JsSIP.C.causes.CANCELED:
                    reason = 'Llamada cancelada';
                    // 487 Request Terminated usually comes here when we cancel
                    break;
                case JsSIP.C.causes.NO_ANSWER:
                    reason = 'Sin respuesta';
                    break;
                case JsSIP.C.causes.BYE:
                case JsSIP.C.causes.TERMINATED:
                    reason = 'Llamada finalizada';
                    break;
                case JsSIP.C.causes.CONNECTION_ERROR:
                    reason = 'Error de conexión';
                    break;
                case JsSIP.C.causes.SERVER_ERROR:
                    reason = 'Error del servidor';
                    if (sipCode) reason += ` (${sipCode})`;
                    break;  
                default:
                    // SIP Code Mapping for more detail if generic failure
                    if (sipCode) {
                        switch(parseInt(sipCode)) {
                            case 403: reason = 'Prohibido (403)'; break;
                            case 404: reason = 'Número no existe (404)'; break;
                            case 480: reason = 'No disponible (480)'; break;
                            case 486: reason = 'Ocupado (486)'; break;
                            case 487: reason = 'Llamada cancelada'; break; 
                            case 488: reason = 'No aceptable (488)'; break;
                            case 500: reason = 'Error interno servidor (500)'; break;
                            case 503: reason = 'Servicio no disponible (503)'; break;
                            case 603: reason = 'Rechazado por destino (603)'; break;
                            default: 
                                reason = wasEstablished ? 'Llamada finalizada' : `Finalizado (${sipCode})`;
                                break;
                        }
                    } else if (!eventCause) {
                         reason = 'Llamada finalizada';
                    } else {
                         reason = wasEstablished ? 'Llamada finalizada' : `Finalizado (${eventCause})`;
                    }
                    break;
            }


            setCallSummary(`${reason} - ${callerNum} (${duration})`);
            setTimeout(() => setCallSummary(null), 5000);
        }

        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
        if (session && session.connection) {
            session.connection.getSenders().forEach(s => s.track && s.track.stop());
            session.connection.getReceivers().forEach(r => r.track && r.track.stop());
        }
    }, [formatTime, stopRingtone]);



    const handleDigit = useCallback((digit) => {
        playDTMFLocally(digit);
        if (activeSession && (inCall || isCalling)) {
            activeSession.sendDTMF(digit);
        } else if (!inCall && !isCalling) {
            setDialNumber(prev => prev + digit);
        }
    }, [activeSession, inCall, isCalling, playDTMFLocally, setDialNumber]);

    const handleBackspace = useCallback(() => {
        if (!inCall && !isCalling) {
            setDialNumber(prev => prev.slice(0, -1));
        }
    }, [inCall, isCalling, setDialNumber]);

    useEffect(() => {
        const onGlobalKey = (e) => {
            if (minimized) return;

            const activeElement = document.activeElement;
            const isInputFocused = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.isContentEditable
            );

            const key = e.key;

            // 1. Priority Actions (Work even if focused on an input)
            if (key === 'Enter' && isRinging) {
                e.preventDefault();
                if (sessionRef.current && !sessionRef.current.isEnded()) {
                    try {
                        sessionRef.current.answer({ mediaConstraints: { audio: true, video: false } });
                    } catch (err) { }
                }
                return;
            }

            if (key === 'Escape') {
                if (inCall || isCalling) {
                    if (activeSession && !activeSession.isEnded()) activeSession.terminate();
                } else if (isRinging) {
                    if (sessionRef.current && !sessionRef.current.isEnded()) sessionRef.current.terminate();
                }
                return;
            }

            // 2. Form Protection: Don't intercept typing if focus is in a form
            if (isInputFocused) return;

            // 3. Global Dialer/DTMF (Only if NOT in an input)
            if (dtmfFrequencies[key]) {
                e.preventDefault();
                handleDigit(key);
            } else if (key === 'Enter' && !inCall && !isCalling) {
                e.preventDefault();
                makeCall();
            } else if (key === 'Backspace' && !inCall && !isCalling) {
                e.preventDefault();
                handleBackspace();
            }
        };
        window.addEventListener('keydown', onGlobalKey);
        return () => window.removeEventListener('keydown', onGlobalKey);
    }, [minimized, inCall, isCalling, isRinging, handleDigit, handleBackspace, makeCall, activeSession]);

    const toggleMute = useCallback(() => {
        if (!activeSession) return;
        if (muted) activeSession.unmute(); else activeSession.mute();
        setMuted(!muted);
    }, [activeSession, muted]);

    const toggleHold = useCallback(() => {
        if (!activeSession) return;
        if (onHold) activeSession.unhold(); else activeSession.hold();
        setOnHold(!onHold);
    }, [activeSession, onHold]);

    // Initial SIP setup
    useEffect(() => {
        if (!user || !isAuthenticated) return;

        let ua = null;
        let socket = null;

        const handleUnload = (e) => {
            if (isAuthenticated) {
                const message = "Esta acción lo desconectará del sistema y se cortará cualquier comunicación en curso. ¿Está seguro?";
                e.preventDefault();
                e.returnValue = message;
                return message;
            }
        };

        const handleFinalExit = () => {
            if (sessionRef.current && !sessionRef.current.isEnded()) sessionRef.current.terminate();
            if (uaRef.current) uaRef.current.stop();
        };

        const initSoftphone = async () => {
            let credentials = null;

            if (user.sip_extension) {
                credentials = user.sip_extension;
            } else {
                try {
                    const response = await userService.getSipCredentials();
                    if (response.status === 'success' && response.data.payload) {
                        const token = localStorage.getItem('token');
                         if (token) {
                            credentials = await decryptPayload(response.data.payload, token);
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch SIP credentials", err);
                }
            }

            if (!credentials) return;

            setExtension(credentials.extension || credentials.sip_user);

            // Define setupSessionEvents locally to avoid re-render issues
            const setupSessionEvents = (session) => {
                session.on('progress', (e) => {
                     unlockAudio();
                     setTimeout(() => {
                        forceAudioAttach();
                        // Decide between local ringback and early media
                        if (e.originator === 'remote' && session.direction === 'outgoing') {
                            const hasRemoteAudio = session.connection && 
                                session.connection.getRemoteStreams().some(s => s.getAudioTracks().length > 0);
                            
                            if (hasRemoteAudio) {
                                stopRingtone();
                            } else {
                                playRingtone();
                            }
                        }
                     }, 500);
                });
                session.on('confirmed', () => {

                    setInCall(true);
                    setIsCalling(false);
                    setIsRinging(false);
                    stopRingtone();
                    setSeconds(0);
                    secondsRef.current = 0;
                    unlockAudio();
                    setTimeout(() => forceAudioAttach(), 500);
                    notification.destroy('incoming_call');
                });
                session.on('ended', (e) => {

                    handleCallEnded(session, e);
                });
                session.on('failed', (e) => {

                    handleCallEnded(session, e);
                });
            };
    
            window.addEventListener('beforeunload', handleUnload);
            window.addEventListener('pagehide', handleFinalExit);
    
            socket = new JsSIP.WebSocketInterface(sipConfig.wss_url);
            const configuration = {
                sockets: [socket],
                uri: `sip:${credentials.sip_user}@${sipConfig.realm}`,
                password: credentials.sip_pass,
                display_name: user.name,
                register: true,
                session_timers: true,
                pcConfig: { iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }] }
            };
    
            ua = new JsSIP.UA(configuration);
            uaRef.current = ua;
            JsSIP.debug.disable('JsSIP:*');
    
            ua.on('registered', () => setStatus('registered'));
            ua.on('registrationFailed', () => setStatus('error'));
            ua.on('newRTCSession', (data) => {
                const session = data.session;
                sessionRef.current = session;
                setActiveSession(session);
                session.on('sdp', (ev) => { 
                    // Manual SDP manipulation to prioritize opus
                    if (ev.sdp) {
                        let lines = ev.sdp.split(/\r?\n/);
                        let mAudioIndex = lines.findIndex(line => line.indexOf('m=audio') === 0);
                        if (mAudioIndex !== -1) {
                            let mLine = lines[mAudioIndex];
                            let elements = mLine.trim().split(/\s+/);
                            if (elements.length > 3) {
                                let payloads = elements.slice(3);
                                let opusPayload = null;
                                for (let line of lines) {
                                    if (line.indexOf('a=rtpmap:') === 0 && line.toLowerCase().includes('opus')) {
                                        let match = line.match(/a=rtpmap:(\d+)\s+opus/i);
                                        if (match) { opusPayload = match[1]; break; }
                                    }
                                }
                                if (opusPayload) {
                                     payloads = payloads.filter(p => p !== opusPayload);
                                     payloads.unshift(opusPayload);
                                     lines[mAudioIndex] = `${elements[0]} ${elements[1]} ${elements[2]} ${payloads.join(' ')}`;
                                }
                            }
                        }
                        ev.sdp = lines.join('\r\n');
                    }
                });
    
                session.on('track', (e) => {
                    if (remoteAudioRef.current && e.track.kind === 'audio') {
                        remoteAudioRef.current.srcObject = new MediaStream([e.track]);
                        remoteAudioRef.current.play().catch(() => {});
                    }
                });
    
                if (session.direction === 'incoming') {
                    setIsRinging(true);
                    playRingtone();
                    setMinimized(false);
                    setDialNumber(session.remote_identity.uri.user);
                } else {
                    // Outgoing: start local ringback fallback after 3s if no progress/vibration
                    const ringbackTimer = setTimeout(() => playRingtone(), 3000);
                    session.on('ringing', () => {
                        clearTimeout(ringbackTimer);
                        playRingtone();
                    });
                    const clearRingback = () => {
                        clearTimeout(ringbackTimer);
                        stopRingtone();
                    };
                    session.on('confirmed', clearRingback);
                    session.on('failed', clearRingback);
                    session.on('ended', clearRingback);
                    session.on('progress', () => clearTimeout(ringbackTimer));
                }
                setupSessionEvents(session);
            });
    
            ua.start();
            credentials = null;
        };

        initSoftphone();

        return () => {
             window.removeEventListener('beforeunload', handleUnload);
             window.removeEventListener('pagehide', handleFinalExit);
             if (uaRef.current) {
                if (sessionRef.current && !sessionRef.current.isEnded()) {
                    sessionRef.current.terminate();
                }
                uaRef.current.stop();
            }
        };
    }, [user, isAuthenticated]);

    useEffect(() => {
        let timer;
        if (inCall && !onHold) {
            timer = setInterval(() => {
                setSeconds(prev => {
                    const newVal = prev + 1;
                    secondsRef.current = newVal;
                    return newVal;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [inCall, onHold]);

    const renderDialpad = () => (
        <div className="softphone-content-view">
             {customDisplay && (
                <div style={{ textAlign: 'center', marginBottom: 10, fontWeight: 'bold', color: '#1890ff' }}>
                    {customDisplay}
                </div>
            )}
            <div className="dialpad-display" style={{ marginBottom: 5, display: 'flex', alignItems: 'center' }}>
                <Input 
                    variant="borderless" 
                    value={dialNumber} 
                    onChange={e => {
                        const val = e.target.value;
                        const filtered = val.split('').filter(char => dtmfFrequencies[char]).join('');
                        if (filtered.length > dialNumber.length) {
                            const lastChar = filtered.slice(-1);
                            playDTMFLocally(lastChar);
                        }
                        setDialNumber(filtered);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !inCall && !isCalling && dialNumber) {
                            e.preventDefault();
                            makeCall();
                        }
                    }}
                    style={{ fontSize: 24, textAlign: 'center', flex: 1 }} 
                    placeholder={customDisplay ? "..." : "Marcar número..."}
                    disabled={!!customDisplay} 
                />
                {dialNumber && (
                    <Button 
                        type="text" 
                        size="large"
                        icon={<ArrowLeftOutlined style={{ fontSize: 20, color: '#8c8c8c' }} />} 
                        onClick={handleBackspace} 
                        style={{ marginLeft: -40 }}
                    />
                )}
            </div>
            {callSummary && (
                <div style={{ textAlign: 'center', marginBottom: 10 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{callSummary}</Text>
                </div>
            )}
            <div className="dialpad-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map(digit => (
                    <button key={digit} className="dialpad-btn" onClick={() => handleDigit(digit.toString())}>{digit}</button>
                ))}
            </div>
            <div style={{ marginTop: 20 }}>
                <Button type="primary" block size="large" shape="round" icon={<PhoneOutlined />} 
                    style={{ background: '#52c41a', borderColor: '#52c41a', height: 45 }} 
                    onClick={makeCall} disabled={status !== 'registered'}>
                    Llamar
                </Button>
            </div>
        </div>
    );

    const renderIncomingCallView = () => (
        <div className="softphone-content-view" style={{ textAlign: 'center' }}>
            <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 5 }}>
                    <Badge status="processing" text="Llamada Entrante..." />
                </div>
                <Title level={4} style={{ margin: '10px 0' }}>{dialNumber}</Title>
                <div style={{ color: '#52c41a', fontSize: 16, marginBottom: 20 }}>Timbrando...</div>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PhoneOutlined className="vibrating-phone" style={{ fontSize: 48, color: '#52c41a', opacity: 0.7 }} />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                <Space size="large">
                    <Tooltip title="Contestar">
                        <Button 
                            shape="circle" 
                            size="large" 
                            type="primary" 
                            icon={<PhoneOutlined />} 
                            style={{ background: '#52c41a', borderColor: '#52c41a', width: 60, height: 60, fontSize: 24 }}
                            onClick={() => {
                                if (sessionRef.current) {
                                    sessionRef.current.answer({
                                        mediaConstraints: { audio: true, video: false }
                                    });
                                }
                            }}
                        />
                    </Tooltip>
                    
                    <Tooltip title="Rechazar">
                        <Button 
                            shape="circle" 
                            size="large" 
                            danger 
                            type="primary" 
                            icon={<PhoneOutlined rotate={225} />} 
                            style={{ width: 60, height: 60, fontSize: 24 }}
                            onClick={() => {
                                if (sessionRef.current && !sessionRef.current.isEnded()) {
                                    sessionRef.current.terminate();
                                }
                            }}
                        />
                    </Tooltip>
                </Space>
            </div>
        </div>
    );

    const renderCallView = (isOutgoing) => (
        <div className="softphone-content-view" style={{ textAlign: 'center' }}>
            <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 5 }}>
                    <Badge status="processing" text={onHold ? "En Espera" : (isOutgoing ? "Llamando..." : "En Llamada")} />
                </div>
                <Title level={4} style={{ margin: '10px 0' }}>{customDisplay || dialNumber}</Title>
                
                <div className="call-timer" style={{ fontSize: 32, marginBottom: 0 }}>
                    {isOutgoing ? "-- : --" : formatTime(seconds)}
                </div>
            </div>
            
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {showKeypad && (
                    <div className="dialpad-grid small">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map(digit => (
                            <button key={digit} className="dialpad-btn" onClick={() => handleDigit(digit.toString())}>{digit}</button>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Space size="large">
                    <Tooltip title={muted ? "Activar Micro" : "Silenciar"}>
                        <Button shape="circle" size="large" icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />} 
                            onClick={toggleMute} danger={muted} type={muted ? 'primary' : 'default'} />
                    </Tooltip>
                    
                    {!isOutgoing && (
                        <Tooltip title={onHold ? "Reanudar" : "Poner en Espera"}>
                            <Button shape="circle" size="large" icon={onHold ? <PlayCircleOutlined /> : <PauseOutlined />} 
                                onClick={toggleHold} type={onHold ? 'primary' : 'default'} />
                        </Tooltip>
                    )}

                    <Tooltip title="Teclado DTMF">
                        <Button shape="circle" size="large" icon={<TableOutlined />} 
                                onClick={() => setShowKeypad(!showKeypad)} type={showKeypad ? 'primary' : 'default'} />
                    </Tooltip>

                    <Button shape="circle" size="large" danger type="primary" icon={<PhoneOutlined rotate={225} />} 
                        onClick={() => activeSession && !activeSession.isEnded() && activeSession.terminate()} />
                </Space>
            </div>
        </div>
    );

    return (
        <div className={`softphone-wrapper ${minimized ? 'is-minimized' : 'is-maximized'}`}>
            {/* El disparador circular siempre está presente pero se oculta vía CSS */}
            <div 
                className={`softphone-minimized-trigger ${isRinging ? 'ringing-animation' : ''}`} 
                onClick={() => {
                    if (!extension) {
                        message.warning('No hay extensión asociada a este usuario');
                        return;
                    }
                    setMinimized(false);
                }}
            >
                <Badge dot={inCall || isCalling || isRinging} color="#52c41a">
                    <Button 
                        type="primary" 
                        shape="circle" 
                        size="large" 
                        icon={<PhoneOutlined />} 
                        style={{ 
                            height: 60, 
                            width: 60, 
                            background: !extension ? '#8c8c8c' : (status !== 'registered' ? '#8c8c8c' : 
                                        isRinging ? '#52c41a' : 
                                        (inCall || isCalling) ? '#ff4d4f' : '#52c41a'),
                            borderColor: !extension ? '#8c8c8c' : (status !== 'registered' ? '#8c8c8c' : 
                                         isRinging ? '#52c41a' : 
                                         (inCall || isCalling) ? '#ff4d4f' : '#52c41a')
                        }} 
                    />
                </Badge>
            </div>

            {/* La tarjeta siempre está presente pero su visibilidad se maneja por CSS */}
            <Card 
                className={`softphone-container ${ isRinging ? 'mode-ringing' : (inCall || isCalling) ? 'mode-call' : 'mode-dialpad' } ${showKeypad ? 'keypad-open' : ''} ${status === 'error' ? 'status-error' : ''} ${isRinging ? 'ringing-accent' : ''}`}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                         <Space align="center" size={4}>
                             <div style={{ width: 8, height: 8, borderRadius: '50%', background: status === 'registered' ? '#52c41a' : 'red' }} />
                             <Text strong>Extensión {extension || '...'}</Text>
                        </Space>
                        <Button type="text" icon={<MinusOutlined />} onClick={() => setMinimized(true)} />
                    </div>
                }
                styles={{ body: { padding: '15px 25px' } }}
            >
                {isRinging ? renderIncomingCallView() : (inCall || isCalling) ? renderCallView(isCalling) : renderDialpad()}
            </Card>

            {/* Audio persistente */}
            <audio 
                ref={remoteAudioRef} 
                autoPlay 
                style={{ position: 'fixed', bottom: 0, right: 0, width: 1, height: 1, opacity: 0.01, zIndex: -1 }} 
            />
        </div>
    );
};

export default Softphone;
